import { useState } from 'preact/hooks';
import { api } from '../api/client';
import { t } from '../i18n';
import { describeAuthError } from '../lib/errors';
import { useLang } from '../lib/hooks';
import { ErrorBox, Field, Notice } from '../components/ui';

type Mode = 'login' | 'register';

export function LoginPage({
  onAuthenticated,
  offline = false,
  onRetry,
}: {
  onAuthenticated: () => void;
  offline?: boolean;
  onRetry?: () => void;
}) {
  useLang();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: Event) => {
    event.preventDefault();
    if (busy) return;

    if (mode === 'register' && password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await api.login(email.trim(), password);
      } else {
        await api.register(email.trim(), password, displayName.trim() || undefined);
      }
      onAuthenticated();
    } catch (caught) {
      setError(describeAuthError(caught));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
  };

  return (
    <div class="auth">
      <div class="auth__card card">
        <h1 class="auth__title">{t('app.name')}</h1>
        <p class="muted auth__tagline">{t('app.tagline')}</p>

        <div class="tabs" role="tablist" aria-label={t('auth.login')}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            class={`tabs__btn${mode === 'login' ? ' is-active' : ''}`}
            onClick={() => setMode('login')}
          >
            {t('auth.login')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            class={`tabs__btn${mode === 'register' ? ' is-active' : ''}`}
            onClick={() => setMode('register')}
          >
            {t('auth.register')}
          </button>
        </div>

        {offline ? (
          <Notice tone="warn">
            {t('app.bootFailed')} {t('app.bootHint')}
            {onRetry ? (
              <>
                {' '}
                <button type="button" class="linklike" onClick={onRetry}>
                  {t('common.retry')}
                </button>
              </>
            ) : null}
          </Notice>
        ) : null}

        <form class="form" onSubmit={submit} noValidate>
          <Field label={t('auth.email')} htmlFor="auth-email">
            <input
              id="auth-email"
              type="email"
              name="email"
              autocomplete="username"
              required
              value={email}
              onInput={(event) => setEmail(event.currentTarget.value)}
            />
          </Field>

          <Field label={t('auth.password')} htmlFor="auth-password">
            <input
              id="auth-password"
              type="password"
              name="password"
              autocomplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'register' ? 8 : undefined}
              value={password}
              onInput={(event) => setPassword(event.currentTarget.value)}
            />
          </Field>

          {mode === 'register' ? (
            <Field label={t('auth.displayName')} hint={t('common.optional')} htmlFor="auth-name">
              <input
                id="auth-name"
                type="text"
                name="display_name"
                autocomplete="nickname"
                maxLength={120}
                value={displayName}
                onInput={(event) => setDisplayName(event.currentTarget.value)}
              />
            </Field>
          ) : null}

          {error ? <ErrorBox message={error} /> : null}

          <button class="btn btn--primary btn--wide" type="submit" disabled={busy || email === '' || password === ''}>
            {busy
              ? mode === 'login'
                ? t('auth.loggingIn')
                : t('auth.registering')
              : mode === 'login'
                ? t('auth.loginAction')
                : t('auth.registerAction')}
          </button>
        </form>

        <button type="button" class="linklike" onClick={switchMode}>
          {mode === 'login' ? t('auth.noAccount') : t('auth.haveAccount')}
        </button>

        {mode === 'register' ? (
          <p class="muted auth__note">{t('auth.registerDisabled')}</p>
        ) : null}
      </div>
    </div>
  );
}
